import React, { useState } from 'react';
import { LayoutDashboard, Package, Truck, History, Settings, Bell, Search, User, LogOut, Store } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export interface MockUser {
  id: string;
  name: string;
  role: string;
}
interface POSLayoutProps {
  children: React.ReactNode;
  activeTab: string;
  onTabChange: (tab: string) => void;
  currentUser: MockUser;
  users: MockUser[];
  onUserChange: (user: MockUser) => void;
}
export function POSLayout({
  children,
  activeTab,
  onTabChange,
  currentUser,
  users,
  onUserChange
}: POSLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const navItems = [{
    id: 'dashboard',
    label: 'Order Queue',
    icon: LayoutDashboard
  }, {
    id: 'history',
    label: 'Order History',
    icon: History
  }, {
    id: 'settings',
    label: 'Settings',
    icon: Settings
  }];
  return <div className="flex h-screen bg-[#F1F5F9] font-sans text-slate-900 overflow-hidden" data-id="element-762" data-mp="1ep8f6kvoazjq">
      {/* Sidebar */}
      <aside className={cn('bg-[#0F172A] text-white flex flex-col transition-all duration-300', isSidebarOpen ? 'w-64' : 'w-20')} data-id="element-763" data-mp="14t5fdloanczy">
        <div className="p-6 flex items-center gap-3 border-b border-slate-800" data-id="element-764" data-mp="19pm34erkzpci">
          <div className="bg-blue-600 p-2 rounded-lg" data-id="element-765" data-mp="1xicccg83nuj3">
            <Store className="w-6 h-6" data-id="element-766" data-mp="22h5oo5bnhfho" />
          </div>
          {isSidebarOpen && <div className="overflow-hidden whitespace-nowrap" data-id="element-767" data-mp="1oh4oks37mc39">
              <h1 className="font-bold text-lg leading-tight" data-id="element-768" data-mp="1oixm9hvuhlz4">Innova Health</h1>
              <p className="text-xs text-slate-400" data-id="element-769" data-mp="1sgvektkwwo6q">Branch: Riyadh Central</p>
            </div>}
        </div>

        <nav className="flex-1 py-6 px-3 space-y-2" data-id="element-770" data-mp="d3skcg3jfrgk">
          {navItems.map(item => <button key={item.id} onClick={() => onTabChange(item.id)} className={cn('w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors', activeTab === item.id ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white')} data-id="element-771" data-mp="h4m4qzhruo4p">
              <item.icon className="w-6 h-6 shrink-0" data-id="element-772" data-mp="24yzfcd5gbjri" />
              {isSidebarOpen && <span className="font-medium" data-id="element-773" data-mp="210rw9bnim6d5">{item.label}</span>}
            </button>)}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden" data-id="element-774" data-mp="1r2c5xawbb14p">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 shrink-0" data-id="element-775" data-mp="1m2zmz18kae7e">
          <div className="flex items-center gap-4 flex-1 max-w-xl" data-id="element-776" data-mp="3le0rhvx1u1y2">
            <div className="relative w-full" data-id="element-777" data-mp="3gfphnl6r7rfu">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-778" data-mp="3gh0h58t62d15" />
              <input type="text" placeholder="Search orders, customers, or items..." className="w-full pl-10 pr-4 py-2 bg-slate-100 border-transparent focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg text-sm transition-all outline-none" data-id="element-779" data-mp="3owzite4hnjrw" />
            </div>
          </div>
          <div className="ml-6 flex items-center gap-3" data-id="element-780" data-mp="35zf4h0zco3lj">
            <Bell className="h-5 w-5 text-slate-400" aria-hidden="true" data-id="element-781" data-mp="39zz0g186t163" />
            <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2 py-1.5" data-id="element-782" data-mp="3a08qtpx59acb">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-700" data-id="element-783" data-mp="3f1en1xq0jfql"><User className="h-4 w-4" data-id="element-784" data-mp="3jw245biot7v5" /></span>
              <span className="hidden text-right leading-tight lg:block" data-id="element-785" data-mp="u9mbkhtnj2k7"><span className="block text-xs font-bold text-slate-800" data-id="element-786" data-mp="ub70nrx75tao">{currentUser.name}</span><span className="block text-[10px] text-slate-500" data-id="element-787" data-mp="z62zbawxvewr">{currentUser.role}</span></span>
              <select value={currentUser.id} onChange={event => {
              const nextUser = users.find(user => user.id === event.target.value);
              if (nextUser) onUserChange(nextUser);
            }} aria-label="Switch logged-in user" className="max-w-[126px] bg-transparent text-xs font-bold text-slate-600 outline-none" data-id="element-788" data-mp="2854dbuh795zl">
                {users.map(user => <option key={user.id} value={user.id} data-id="element-789" data-mp="2m5f6j45zewnx">{user.name}</option>)}
              </select>
            </label>
          </div>
        </header>

        {/* Page Area */}
        <div className="flex-1 overflow-auto p-8" data-id="element-790" data-mp="2vuf9eynluc7e">{children}</div>
      </main>
    </div>;
}